/**
 * UnderlineTabs — secondary-level tabs (Webex underline style).
 *
 * Visual contrast vs. <PillTabs>:
 *   • No background pill; tabs are flat text on a shared baseline rule.
 *   • Active tab is indicated by a 2px underline overlapping the rule.
 *   • Used inside a section/page that already has a higher-level <PillTabs>
 *     above it (see /dev/components → "Tabs" example).
 *
 * Design system mapping:
 *   • List:  border-b webex-gray-200 dark:webex-gray-800 · gap-6 · h-10
 *   • Tab:   text-sm font-medium · pb-2.5 · -mb-px (overlap rule)
 *           inactive: text-webex-gray-700 dark:text-webex-gray-300
 *           active:   text-webex-gray-1000 dark:text-white +
 *                     2px bottom border webex-gray-1000 / dark:white
 *   • Panel: outline-none focus-visible ring (matches PillTabs)
 */
import * as React from "react"
import { Tabs as BaseTabs } from "@base-ui-components/react/tabs"
import { cn } from "@lib/cn"

const Root = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof BaseTabs.Root>
>(({ className, ...props }, ref) => (
  <BaseTabs.Root ref={ref} className={cn("flex flex-col gap-4", className)} {...props} />
))
Root.displayName = "UnderlineTabs.Root"

const List = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof BaseTabs.List>
>(({ className, ...props }, ref) => (
  <BaseTabs.List
    ref={ref}
    className={cn(
      "inline-flex items-end gap-6",
      "border-b border-webex-gray-200 dark:border-webex-gray-800",
      className,
    )}
    {...props}
  />
))
List.displayName = "UnderlineTabs.List"

const Tab = React.forwardRef<
  HTMLButtonElement,
  React.ComponentPropsWithoutRef<typeof BaseTabs.Tab>
>(({ className, ...props }, ref) => (
  <BaseTabs.Tab
    ref={ref}
    className={cn(
      "inline-flex items-center gap-1.5 px-0.5 pb-2.5 -mb-px",
      "text-sm font-medium leading-tight",
      "bg-transparent border-0 border-b-2 border-transparent cursor-pointer select-none",
      "text-webex-gray-700 dark:text-webex-gray-300",
      "transition-colors duration-150 motion-reduce:transition-none",
      "hover:text-webex-gray-1000 dark:hover:text-white",
      "aria-selected:text-webex-gray-1000 aria-selected:border-webex-gray-1000",
      "dark:aria-selected:text-white dark:aria-selected:border-white",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-webex-blue-600 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-webex-gray-950 focus-visible:rounded-sm",
      "[&>svg]:size-4 [&>img]:size-4",
      className,
    )}
    {...props}
  />
))
Tab.displayName = "UnderlineTabs.Tab"

const Panel = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof BaseTabs.Panel>
>(({ className, ...props }, ref) => (
  <BaseTabs.Panel
    ref={ref}
    className={cn(
      "outline-none focus-visible:ring-2 focus-visible:ring-webex-blue-600 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-webex-gray-950 rounded",
      className,
    )}
    {...props}
  />
))
Panel.displayName = "UnderlineTabs.Panel"

export const UnderlineTabs = { Root, List, Tab, Panel }
